import { convexQuery } from "@convex-dev/react-query";
import { isValidConvexId } from "@plasma/utils/convex-id";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { AlertCircle, Book, Info } from "lucide-react";

import {
  categoriesGetById,
  itemsGetForCategory,
  menusGetById,
  modifiersGetAllGroupsForCategory,
  modifiersGetAllGroupsForCategoryItems,
} from "~/api-refs";
import { AddMenuElementButton } from "~/components/menus/add-menu-element-button";
import { EditCategoryButton } from "~/components/menus/categories/edit-category-button";
import { ItemCard } from "~/components/menus/items/item-card";
import { MenuPageHeader } from "~/components/menus/menu-header";
import { ModifierGrid } from "~/components/menus/modifier-grid";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/components/ui/empty";
import { toConvexTableId } from "~/lib/convex-table-id";

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/categories/$categoryId"
)({
  component: CategoryPage,
});

function CategoryPage() {
  const { menuId, categoryId } = Route.useParams();

  // Validate ID formats before making any queries
  const validMenuId = isValidConvexId(menuId);
  const validCategoryId = isValidConvexId(categoryId);
  const validIds = validMenuId && validCategoryId;

  const { data: category, isPending: categoryLoading } = useQuery(
    convexQuery(
      categoriesGetById,
      validIds
        ? {
            id: toConvexTableId("categories", categoryId),
            includeMenus: true,
          }
        : "skip"
    )
  );
  const { data: menu, isPending: menuLoading } = useQuery(
    convexQuery(
      menusGetById,
      validIds ? { id: toConvexTableId("menus", menuId) } : "skip"
    )
  );

  if (!(validMenuId && validCategoryId)) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{validMenuId ? "Category" : "Menu"} Not Found</AlertTitle>
        <AlertDescription className="flex flex-col gap-3">
          <span>
            The {validMenuId ? "category" : "menu"} ID in the URL is invalid or
            doesn't exist.
          </span>
          <Button asChild className="w-fit" size="sm" variant="outline">
            <Link to="/menus">Back to Menus</Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (categoryLoading || menuLoading) {
    return <LoadingPanel message="Loading category details..." />;
  }

  if (!(category && menu)) {
    throw new Error("Category or menu not found");
  }

  return (
    <>
      <MenuPageHeader
        crumbs={[
          {
            label: menu.name,
            to: `/menus/${menuId}/categories`,
          },
          {
            label: category.name,
            to: `/menus/${menuId}/categories/${categoryId}`,
          },
        ]}
        menuId={menuId}
      >
        <EditCategoryButton categoryId={categoryId} menuId={menuId} />
      </MenuPageHeader>
      <div className="space-y-6 pt-3">
        {category.menuRelations && category.menuRelations.length > 1 && (
          <div className="bg-info/10 flex items-start gap-2 rounded-2xl p-3">
            <Info className="text-info size-5" />
            <div className="space-y-1">
              <p className="label text-foreground">Shared category</p>
              <div className="flex flex-wrap items-center gap-1">
                {category.menuRelations.map(
                  (menuRelation: { menu: { name: string } }) => (
                    <p
                      className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                      key={menuRelation.menu.name}
                    >
                      {menuRelation.menu.name}
                    </p>
                  )
                )}
              </div>
            </div>
          </div>
        )}
        <ItemsSection categoryId={categoryId} menuId={menuId} />
        <CategoryModifierSection categoryId={categoryId} menuId={menuId} />
        <ItemModifierSection categoryId={categoryId} menuId={menuId} />
      </div>
      {/* Outlet for child routes (e.g., /edit modal) */}
      <Outlet />
    </>
  );
}

const CategoryModifierSection = ({
  categoryId,
  menuId,
}: {
  categoryId: string;
  menuId: string;
}) => {
  return (
    <section className="space-y-3">
      <h2 className="subtitle font-heading text-foreground">
        Category Modifiers
      </h2>
      <CategoryModifierSectionContent categoryId={categoryId} menuId={menuId} />
    </section>
  );
};

const ItemModifierSection = ({
  categoryId,
  menuId,
}: {
  categoryId: string;
  menuId: string;
}) => {
  const { data: modifierGroups, isPending } = useQuery(
    convexQuery(modifiersGetAllGroupsForCategoryItems, {
      categoryId: toConvexTableId("categories", categoryId),
    })
  );

  if (isPending || !modifierGroups) {
    return <LoadingPanel message="Loading item modifiers..." />;
  }

  if (modifierGroups.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <h2 className="subtitle font-heading text-foreground">Item Modifiers</h2>
      <ModifierGrid menuId={menuId} modifierGroups={modifierGroups} />
    </section>
  );
};

const CategoryModifierSectionContent = ({
  categoryId,
  menuId,
}: {
  categoryId: string;
  menuId: string;
}) => {
  const { data: modifierGroups, isPending } = useQuery(
    convexQuery(modifiersGetAllGroupsForCategory, {
      categoryId: toConvexTableId("categories", categoryId),
    })
  );

  if (isPending || !modifierGroups) {
    return <LoadingPanel message="Loading category modifiers..." />;
  }

  if (modifierGroups.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Book />
          </EmptyMedia>
          <EmptyTitle>Add modifiers</EmptyTitle>
          <EmptyDescription>
            Quickly apply modifiers to all items in this category
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <AddMenuElementButton menuElement="modifier" />
        </EmptyContent>
      </Empty>
    );
  }

  return <ModifierGrid menuId={menuId} modifierGroups={modifierGroups} />;
};

const ItemsSection = ({
  menuId,
  categoryId,
}: {
  menuId: string;
  categoryId: string;
}) => {
  return (
    <section className="space-y-3">
      <h2 className="subtitle font-heading text-foreground">Items</h2>
      <ItemsSectionContent categoryId={categoryId} menuId={menuId} />
    </section>
  );
};

const ItemsSectionContent = ({
  menuId,
  categoryId,
}: {
  menuId: string;
  categoryId: string;
}) => {
  const { data: result, isPending } = useQuery(
    convexQuery(itemsGetForCategory, {
      categoryId: toConvexTableId("categories", categoryId),
      paginationOpts: { numItems: 100, cursor: null },
    })
  );

  if (isPending || !result) {
    return <LoadingPanel message="Loading category items..." />;
  }

  const items = result.page;

  if (!items.length) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Book />
          </EmptyMedia>
          <EmptyTitle>Add items to get started</EmptyTitle>
          <EmptyDescription>
            Organize your items using categories
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <AddMenuElementButton menuElement="item" />
        </EmptyContent>
      </Empty>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(12.5rem,1fr))] gap-4">
      {items.map((item) => (
        <ItemCard item={item} key={item.id} menuId={menuId} />
      ))}
    </div>
  );
};
